import { Order } from "../../Interfaces/Orders";
import ReactDOMServer from "react-dom/server";
interface OrderPrintoutProps {
  order: Order;
  freeShippingThreshold: number;
  baseShippingCost: number;
}

const tableStyles: {
  container: React.CSSProperties;
  table: React.CSSProperties;
  thTd: React.CSSProperties;
} = {
  container: { marginTop: "20px", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" as const },
  thTd: {
    border: "1px solid #ccc",
    padding: "10px",
    textAlign: "left" as const,
  },
};

const columnWidths = [{ width: "60%" }, { width: "20%" }, { width: "20%" }];

const OrderPrintout: React.FC<OrderPrintoutProps> = ({
  order,
  freeShippingThreshold,
  baseShippingCost,
}) => {
  const { customer_name, shipping_info, orderlineitem_set } = order;

  const subtotal = orderlineitem_set.reduce(
    (sum, item) => sum + parseFloat(item.product_cost) * item.quantity,
    0
  );
  const totalItems = orderlineitem_set.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const shippingCost =
    subtotal >= freeShippingThreshold ? 0 : baseShippingCost + totalItems;
  const total = subtotal + shippingCost;

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "800px",
        margin: "auto",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        border: "1px solid #ddd",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          fontSize: "20px",
          fontWeight: "bold",
          marginBottom: "20px",
        }}
      >
        The Make a Memory Family
      </h2>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#f8f8f8",
          padding: "15px",
          borderRadius: "5px",
          marginBottom: "20px",
        }}
      >
        {/* Left Column */}
        <div>
          <p>
            <strong>Order Number:</strong> {order.order_id}
          </p>
          <p>
            <strong>Fundraiser:</strong> {order.fundraiser_name}
          </p>
          <p>
            <strong>Supported Participant:</strong>{" "}
            {order.supported_participant}
          </p>
        </div>

        {/* Right Column */}
        <div>
          <p>
            <strong>Customer:</strong> {customer_name}
          </p>
          <p>
            <strong>Ship To:</strong> {shipping_info?.ship_to_name}
          </p>
          <p>
            {shipping_info?.street_address}
            {shipping_info?.apt_suite
              ? `, ${shipping_info.apt_suite}`
              : ""}, {shipping_info?.city}, {shipping_info?.state}{" "}
            {shipping_info?.zip_code}
          </p>
          {order.phone_number && (
            <p>
              <strong>Phone Number:</strong> {order.phone_number}
            </p>
          )}
        </div>
      </div>

      {/* Order Items Table */}
      <div style={tableStyles.container}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={{ ...tableStyles.thTd, ...columnWidths[0] }}>Item</th>
              <th style={{ ...tableStyles.thTd, ...columnWidths[1] }}>Qty</th>
              <th style={{ ...tableStyles.thTd, ...columnWidths[2] }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {orderlineitem_set.map((item) => (
              <tr key={item.id}>
                <td style={{ ...tableStyles.thTd, ...columnWidths[0] }}>
                  <strong>{item.product.item_display_name}</strong>
                  {item.customization && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      {item.customization.bottle_text && (
                        <p>
                          <strong>Text:</strong>{" "}
                          {item.customization.bottle_text}
                        </p>
                      )}
                      {item.customization.font_style && (
                        <p>
                          <strong>Font:</strong> {item.customization.font_style}
                        </p>
                      )}
                      {item.customization.design && (
                        <p>
                          <strong>Design:</strong> {item.customization.design}
                        </p>
                      )}
                      {item.customization.clothing_size && (
                        <p>
                          <strong>Size:</strong>{" "}
                          {item.customization.clothing_size.replace("_", " ")}
                        </p>
                      )}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    ...tableStyles.thTd,
                    ...columnWidths[1],
                    textAlign: "center",
                  }}
                >
                  {item.quantity}
                </td>
                <td
                  style={{
                    ...tableStyles.thTd,
                    ...columnWidths[2],
                    textAlign: "right",
                  }}
                >
                  ${(+item.product_cost * item.quantity).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Table */}
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            border: "1px solid #ccc",
            width: "250px",
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Subtotal:
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  textAlign: "right",
                }}
              >
                ${subtotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Shipping:
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  textAlign: "right",
                }}
              >
                {shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}
              </td>
            </tr>

            <tr style={{ backgroundColor: "#f8f8f8", fontWeight: "bold" }}>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                Total:
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  textAlign: "right",
                }}
              >
                ${total.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#ddd",
          textAlign: "center",
          padding: "20px",
          borderTop: "1px solid #ccc",
          maxWidth: "100%", // Ensures it does not extend beyond the page width
          wordWrap: "break-word", // Breaks long words if needed
          boxSizing: "border-box", // Ensures padding does not add extra width
        }}
      >
        <p style={{ margin: 0, padding: "0 10px" }}>
          Have questions or want help starting your own fundraiser? Please email{" "}
          <strong>help@themakeamemoryfamily.com</strong>
        </p>
      </div>
    </div>
  );
};

// Function to render it as a string for printing
const renderOrderPrintoutToString = (
  order: any,
  baseShippingCost: number,
  freeShippingThreshold: number
): string => {
  return ReactDOMServer.renderToStaticMarkup(
    <OrderPrintout
      order={order}
      baseShippingCost={baseShippingCost}
      freeShippingThreshold={freeShippingThreshold}
    />
  );
};

export { OrderPrintout, renderOrderPrintoutToString };
